import { makeStyles } from "$components/mui-styles";
import { styled } from "@material-ui/core";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import { SpeedDial, SpeedDialAction, SpeedDialIcon } from "@material-ui/lab";
import { type ReactNode, useState } from "react";
import { useIntl } from "react-intl";
import { useRegisterActionBarHeight } from "./action-bar-offset";
import type { ActionBarVariant, ActionItem } from "./action-item";

// `ActionItem.icon`/`fabIcon` are optional (some consumers only ever rendered
// text buttons), but a SpeedDialAction needs a glyph or it's an empty circle.
// Fall back to a neutral dot; the action's label still shows beside it.
const fallbackIcon = <FiberManualRecordIcon />;

const FAB_SIZE = 56;

// Each variant keeps its declared button colour as the fab background; tertiary
// and quarternary are primary-toned like their inline counterparts.
const VARIANT_COLOR: Record<ActionBarVariant, "primary" | "secondary"> = {
  primary: "primary",
  secondary: "secondary",
  tertiary: "primary",
  quarternary: "primary",
};

const FloatingSpeedDial = styled(SpeedDial)(({ theme }) => ({
  position: "fixed",
  right: theme.spacing(2),
  bottom: theme.spacing(2),
  // Above the base drawer layer but below panels/backdrops (which sit at
  // `drawer + 2`), matching the fixed action bar this replaces.
  zIndex: theme.zIndex.drawer + 1,
}));

// The default SpeedDialAction is a small grey-on-white fab whose icon and label
// are hard to read. Fill it with its declared variant colour (contrasting icon)
// and darken the label so both are legible.
const useActionStyles = makeStyles((theme) => ({
  fab: {
    // Consumers pass icons of varying intrinsic sizes; normalise every one to a
    // fixed square so they sit centred and consistent across all fabs.
    "& svg": {
      width: 24,
      height: 24,
      fontSize: 24,
    },
  },
  fabPrimary: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
    "&.Mui-disabled": {
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
    },
  },
  fabSecondary: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.secondary.dark,
    },
    "&.Mui-disabled": {
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
    },
  },
  staticTooltipLabel: {
    color: theme.palette.text.primary,
    fontWeight: 600,
    fontSize: "0.875rem",
    whiteSpace: "nowrap",
    boxShadow: theme.shadows[2],
  },
}));

// Non-action content (e.g. a count bubble) sits bottom-aligned just to the left
// of the FAB and stays put whether the dial is open or closed.
const TrailingSlot = styled("div")(({ theme }) => ({
  position: "fixed",
  right: FAB_SIZE + theme.spacing(3),
  // Bottom edge aligns with the FAB's bottom edge.
  bottom: theme.spacing(2),
  zIndex: theme.zIndex.drawer + 1,
  display: "flex",
  alignItems: "flex-end",
  gap: theme.spacing(1),
}));

type Props = {
  /** All the bar's actions to fan out; rendered in order, upward from the FAB. */
  actions: ActionItem[];
  /** aria-label for the FAB itself (SpeedDial requires a string). */
  ariaLabel: string;
  /** Non-action content shown to the left of the FAB. */
  trailing?: ReactNode;
};

export const ActionBarSpeedDial = ({ actions, ariaLabel, trailing }: Props) => {
  const intl = useIntl();
  const actionClasses = useActionStyles();
  const [open, setOpen] = useState(false);
  // Reserve just the resting FAB's height via <ActionBarSpacer /> (which adds
  // its own breathing gap) so it can't hide content. Not measured: the dial's
  // root grows when open, which would otherwise jitter the page layout.
  useRegisterActionBarHeight(FAB_SIZE);

  return (
    <>
      {trailing && <TrailingSlot>{trailing}</TrailingSlot>}
      <FloatingSpeedDial
        ariaLabel={ariaLabel}
        icon={<SpeedDialIcon />}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        direction="up"
      >
        {actions.map((action) => {
          const label = intl.formatMessage({
            id: action.id,
            defaultMessage: action.defaultMessage,
          });
          const colorClass =
            VARIANT_COLOR[action.variant ?? "secondary"] === "secondary"
              ? actionClasses.fabSecondary
              : actionClasses.fabPrimary;
          return (
            <SpeedDialAction
              key={action.id}
              classes={{
                fab: `${actionClasses.fab} ${colorClass}`,
                staticTooltipLabel: actionClasses.staticTooltipLabel,
              }}
              icon={action.fabIcon ?? action.icon ?? fallbackIcon}
              tooltipTitle={label}
              tooltipOpen
              FabProps={{
                size: "medium",
                "aria-label": label,
                "data-testid": action.testId,
                disabled: action.disabled,
                onClick: (e) => {
                  action.onClick?.(e);
                  setOpen(false);
                },
              }}
            />
          );
        })}
      </FloatingSpeedDial>
    </>
  );
};
